import { LatLon } from '../domain/types.ts';

export interface OverpassElement {
  type: 'node' | 'way';
  id: number;
  lat?: number;
  lon?: number;
  tags?: Record<string, string>;
  geometry?: Array<{ lat: number; lon: number }>;
}

export interface OverpassResponse {
  elements: OverpassElement[];
}

export class OsmClient {
  private static readonly WORKER_OSM_ENDPOINT = 'https://tradaviahe.tradaviahe.workers.dev/api/osm';

  private static readonly ENDPOINTS = [
    'https://overpass-api.de/api/interpreter',
    'https://lz4.overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass.osm.ch/api/interpreter',
  ];

  public static async fetchNeighborhoodData(
    center: LatLon,
    radiusM = 200,
    timeoutMs = 6000
  ): Promise<OverpassResponse | null> {
    const query = `[out:json][timeout:10];(way["highway"~"primary|secondary|tertiary|residential|pedestrian|living_street|footway|path"](around:${radiusM},${center.lat},${center.lon}););out geom 50;(node["amenity"~"cafe|restaurant|waste_basket"](around:${radiusM},${center.lat},${center.lon}););out 20;`;

    // 1. Thử gọi Cloudflare Edge Proxy trước (nhanh, CORS-free, KV Cache)
    try {
      const workerUrl = `${this.WORKER_OSM_ENDPOINT}?lat=${center.lat}&lon=${center.lon}&radius=${radiusM}`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 7000);

      const res = await fetch(workerUrl, { signal: controller.signal });
      clearTimeout(timer);

      if (res.ok) {
        const data = (await res.json()) as OverpassResponse;
        if (data && Array.isArray(data.elements) && data.elements.length > 0) {
          const wayCount = data.elements.filter((e) => e.type === 'way').length;
          if (wayCount >= 1) {
            return data;
          }
        }
      }
    } catch {
      // Tiếp tục fallback sang mirrors công cộng
    }

    // 2. Thử lần lượt các public Overpass mirrors
    for (const endpoint of this.ENDPOINTS) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);

        const targetUrl = `${endpoint}?data=${encodeURIComponent(query)}`;
        const response = await fetch(targetUrl, {
          method: 'GET',
          signal: controller.signal,
        });

        clearTimeout(timer);

        if (!response.ok) {
          continue;
        }

        const data = (await response.json()) as OverpassResponse;
        if (data && Array.isArray(data.elements) && data.elements.length > 0) {
          const wayCount = data.elements.filter((e) => e.type === 'way').length;
          if (wayCount >= 1) {
            return data;
          }
        }
      } catch {
        // Tiếp tục mirror khác
      }
    }

    return null;
  }
}
